import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCall, REACTIONS } from "@/context/CallContext";
import { StreamVideo } from "@/components/StreamVideo";
import { useFrameModeration } from "@/hooks/useFrameModeration";
import { Avatar } from "@/components/Avatar";
import { toast } from "sonner";
import { Mic, MicOff, Video, VideoOff, SkipForward, Flag, MessageSquare, X, Sparkles, Send, Volume2, VolumeX, Users, Radio, Captions, WifiOff, Languages, Lock, SmilePlus, Crown, Clock, Globe2, RefreshCw, LayoutDashboard, UserPlus, SwitchCamera } from "lucide-react";

const REPORT_REASONS = [
  { id: "nudity", label: "Nudity / sexual" }, { id: "harassment", label: "Harassment" }, { id: "hate", label: "Hate / abuse" },
  { id: "threat", label: "Threat" }, { id: "scam", label: "Scam" }, { id: "spam", label: "Spam" },
  { id: "underage", label: "Underage user" }, { id: "violence", label: "Violence" }, { id: "other", label: "Other" },
];
const RTC_LABEL = { new: "Negotiating", connecting: "Connecting", connected: "P2P · Live", disconnected: "Reconnecting", failed: "Connection failed", closed: "Closed" };
const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const SWIPE_HINT_DELAY_MS = 4000;
const FRIEND_LABELS = { accepted: "Friends ✓", pending: "Request sent ✓" };
const MSG_STYLES = {
  me: "ml-auto bg-cyan-500 text-slate-950",
  system: "mx-auto text-xs font-mono text-slate-400 bg-slate-900/60",
  them: "bg-slate-800 text-slate-100",
};

export default function VideoChat() {
  const nav = useNavigate();
  const { user } = useAuth();
  const call = useCall();
  const { status, online, match, messages, localStream, remoteStream, rtcState, quality, partnerMedia, partnerAway, muted, camOff, lastEvent, captions, captionsOn, partnerWantsCaptions, captionsSupported, reactions, recap, callStartedAt, calling, audioRecovering, canFlip } = call;
  const [flipping, setFlipping] = useState(false);
  const flip = async () => { setFlipping(true); const ok = await call.flipCamera(); setFlipping(false); if (!ok) toast.error("This device can't switch cameras"); };
  const [friendState, setFriendState] = useState(null);
  const [speaker, setSpeaker] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reactOpen, setReactOpen] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [icebreaker, setIcebreaker] = useState("");
  const [duration, setDuration] = useState(0);
  const chatEndRef = useRef(null);
  const exitingRef = useRef(false);
  const touchRef = useRef(null);
  const [dragX, setDragX] = useState(0);
  const [swipeHint, setSwipeHint] = useState(true);
  const partner = match?.match;
  const shared = match?.shared_interests || [];
  const isDemo = match?.mode === "demo";
  useFrameModeration({ active: status === "call" && !!match && !isDemo, localStream, camOff });
  const isPremium = !!user?.premium || user?.role === "admin" || user?.role === "moderator";

  useEffect(() => {
    if (exitingRef.current || recap || status === "calling" || status === "connecting") return;
    if (status === "disconnected" || (status === "idle" && !match)) nav("/match");
  }, [status, match, recap, nav]);

  useEffect(() => { setFriendState(null); }, [recap?.session_id]);

  useEffect(() => {
    if (lastEvent?.type === "call_failed" && !match) { exitingRef.current = true; call.disconnectAll(); nav("/dashboard?tab=friends"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEvent]);

  useEffect(() => {
    if (!match) { setIcebreaker(""); return; }
    setChatOpen(false); setReactOpen(false);
    const tick = () => setDuration(callStartedAt ? Math.round((Date.now() - callStartedAt) / 1000) : 0);
    tick();
    const t = setInterval(tick, 1000);
    api.post("/ai/icebreaker", { interests: shared.length ? shared : partner.interests, country: partner.country })
      .then((r) => setIcebreaker(r.data.prompt))
      .catch(() => setIcebreaker("What's the best thing that happened to you this week?"));
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.session_id, callStartedAt]);

  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.type === "partner_left") toast(lastEvent.reason === "skip" ? "Your partner skipped — finding someone new…" : "Your partner left — finding someone new…");
    if (lastEvent.type === "error" && lastEvent.code === "premium_required") setUpsellOpen(true);
  }, [lastEvent]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const submitReport = async (reason) => {
    try {
      await api.post("/match/report", { target_id: partner.id, reason, session_id: match.session_id });
      toast.success("Report submitted. User blocked.");
      setReportOpen(false);
      call.skip();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rate = async (rating) => {
    try {
      await api.post("/match/rate", { target_id: recap.partner.id, session_id: recap.session_id, rating });
      toast.success("Thanks for the feedback");
    } catch (e) { toast.error(apiError(e)); }
  };

  const exit = () => { exitingRef.current = true; call.disconnectAll(); nav("/dashboard"); };
  const addFriend = async () => {
    try {
      const { data } = await api.post("/friends/request", { target_id: recap.partner.id });
      setFriendState(data.status);
      toast.success(data.status === "accepted" ? `You and ${recap.partner.name} are now friends!` : `Friend request sent to ${recap.partner.name}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const sendMsg = () => { call.sendChat(msg); setMsg(""); };
  const onCaptions = () => {
    if (!isPremium) { setUpsellOpen(true); return; }
    if (!captionsOn && !captionsSupported && isDemo) { toast.error("Live captions need Chrome, Edge or Safari."); return; }
    call.toggleCaptions();
  };
  const myLang = user?.languages?.[0] || "English";
  const theirCaptions = captions.filter((c) => c.from === "them").slice(-2);
  const myCaption = [...captions].reverse().find((c) => c.from === "me");

  useEffect(() => { if (!match) return; setSwipeHint(true); const t = setTimeout(() => setSwipeHint(false), SWIPE_HINT_DELAY_MS); return () => clearTimeout(t); }, [match?.session_id]);
  const onTouchStart = (e) => { if (chatOpen || reportOpen || upsellOpen) return; const t = e.touches[0]; touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() }; };
  const onTouchMove = (e) => {
    if (!touchRef.current) return;
    const t = e.touches[0]; const dx = t.clientX - touchRef.current.x; const dy = t.clientY - touchRef.current.y;
    if (Math.abs(dy) > 70) { touchRef.current = null; setDragX(0); return; }
    if (dx < 0) setDragX(Math.max(dx, -160));
  };
  const onTouchEnd = () => {
    const s = touchRef.current; touchRef.current = null;
    const committed = dragX <= -90 || (s && dragX <= -50 && Date.now() - s.t < 250);
    setDragX(0);
    if (committed) call.skip();
  };

  if (recap) {
    return (
      <div className="min-h-screen orbit-bg-grid relative overflow-hidden flex items-center justify-center p-4" data-testid="session-recap">
        <div className="absolute inset-0 orbit-bg-grid opacity-60" />
        <div className="relative orbit-glass rounded-3xl p-8 max-w-md w-full text-center orbit-fade-up">
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">Call ended</div>
          <div className="mt-4 mx-auto w-20 h-20 rounded-full p-[3px] bg-gradient-to-br from-cyan-400 to-fuchsia-500">
            <Avatar profile={recap.partner} size="w-full h-full" text="text-2xl" className="bg-slate-950" testId="recap-avatar" />
          </div>
          <h1 className="font-display text-3xl font-bold mt-3">Nice chat with {recap.partner.name}</h1>
          {recap.mode === "demo" && <div className="mt-2 inline-block text-[10px] font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-300">DEMO MATCH — Simulated conversation</div>}
          <div className="grid grid-cols-2 gap-3 mt-6 text-left">
            <div className="rounded-2xl bg-slate-900/60 border border-white/10 p-4">
              <div className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Duration</div>
              <div className="font-display text-2xl font-bold mt-1" data-testid="recap-duration">{fmt(recap.duration)}</div>
            </div>
            <div className="rounded-2xl bg-slate-900/60 border border-white/10 p-4">
              <div className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1"><Globe2 className="w-3 h-3" /> Countries</div>
              <div className="text-sm mt-1 leading-6" data-testid="recap-countries">
                <div>{recap.my_flag} {recap.my_country || "You"}</div>
                <div>{recap.partner.flag} {recap.partner.country}</div>
              </div>
            </div>
            <div className="rounded-2xl bg-slate-900/60 border border-white/10 p-4 col-span-2">
              <div className="text-[10px] font-mono uppercase text-slate-500 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Shared interests</div>
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="recap-shared">
                {recap.shared.length ? recap.shared.map((s) => <span key={s} className="orbit-chip active text-xs">{s}</span>) : <span className="text-sm text-slate-500">No overlap — that's what made it interesting</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 mt-5 text-xs text-slate-500">
            <span>How was it?</span>
            <button onClick={() => rate("good")} className="px-2 py-1 rounded-full hover:bg-white/10" data-testid="rate-good">😊</button>
            <button onClick={() => rate("okay")} className="px-2 py-1 rounded-full hover:bg-white/10" data-testid="rate-okay">🙂</button>
            <button onClick={() => rate("bad")} className="px-2 py-1 rounded-full hover:bg-white/10" data-testid="rate-bad">😕</button>
          </div>
          <button onClick={call.connectAgain} className="orbit-btn-primary w-full mt-6 flex items-center justify-center gap-2" data-testid="recap-connect-again-btn">
            <RefreshCw className="w-4 h-4" /> Connect Again
          </button>
          {recap.mode === "live" && (
            <button onClick={addFriend} disabled={!!friendState} className="w-full mt-3 py-3 rounded-full bg-white/5 border border-white/10 text-slate-100 hover:bg-white/10 disabled:opacity-70 flex items-center justify-center gap-2" data-testid="recap-add-friend-btn">
              <UserPlus className="w-4 h-4" /> {FRIEND_LABELS[friendState] || `Add ${recap.partner.name} as friend`}
            </button>
          )}
          <button onClick={exit} className="mt-3 text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="recap-exit-btn"><LayoutDashboard className="w-3 h-3" /> Back to dashboard</button>
        </div>
      </div>
    );
  }

  if (status === "calling" && !match) {
    return (
      <div className="min-h-screen orbit-bg-grid relative overflow-hidden flex items-center justify-center" data-testid="video-calling-overlay">
        <div className="absolute inset-0 orbit-bg-grid opacity-60" />
        <div className="relative text-center">
          <div className="relative w-40 h-40 mx-auto mb-6">
            <div className="orbit-radar-ring" />
            <div className="orbit-radar-ring" style={{ animationDelay: "0.7s" }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center font-display text-3xl font-bold text-slate-950">{calling?.friend?.name?.[0]?.toUpperCase() || "…"}</div>
            </div>
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mb-2">{calling ? "Ringing" : "Connecting"}</div>
          <div className="font-display text-2xl font-bold" data-testid="video-calling-name">{calling ? `Calling ${calling.friend.flag} ${calling.friend.name}…` : "Connecting your call…"}</div>
          <div><button onClick={() => { call.cancelCall(); exit(); }} className="mt-6 orbit-chip inline-flex items-center gap-2 !text-rose-300 !border-rose-500/40" data-testid="video-calling-cancel-btn"><X className="w-3 h-3" /> Cancel</button></div>
        </div>
      </div>
    );
  }

  if (!match || !partner) {
    return (
      <div className="relative overflow-hidden bg-slate-950 flex items-end justify-center" style={{ minHeight: "100dvh" }} data-testid="video-searching-overlay">
        {localStream ? (
          <StreamVideo stream={localStream} muted mirror className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 orbit-bg-grid" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-transparent to-slate-950/85 pointer-events-none" />
        <div className="absolute top-5 left-0 right-0 flex justify-center pointer-events-none">
          <div className="orbit-glass rounded-full px-4 py-1.5 inline-flex items-center gap-2 text-xs font-mono text-emerald-300" data-testid="video-searching-online"><Users className="w-3.5 h-3.5" /> {online} online now</div>
        </div>
        <div className="relative z-10 text-center pb-10 px-6 w-full max-w-md">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="orbit-radar-ring" />
            <div className="orbit-radar-ring" style={{ animationDelay: "0.7s" }} />
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-400 to-fuchsia-500 opacity-80" />
          </div>
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300 mb-1">Scanning Orbit Hello</div>
          <div className="font-display text-2xl sm:text-3xl font-bold drop-shadow-lg">Finding your next match…</div>
          <div><button onClick={exit} className="mt-6 orbit-chip inline-flex items-center gap-2 !bg-slate-950/60" data-testid="video-searching-exit-btn"><X className="w-3 h-3" /> Exit</button></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-950 relative overflow-hidden touch-pan-y" style={{ minHeight: "100dvh" }} onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} data-testid="video-call-screen">
      {/* remote */}
      <div className="absolute inset-x-0 top-0 h-1/2 bg-slate-900 transition-transform duration-75 overflow-hidden" style={{ transform: dragX ? `translateX(${dragX}px) scale(${1 - Math.min(0.08, -dragX / 2000)})` : undefined }} data-testid="video-remote-player">
        {isDemo ? (
          <img src={partner.avatar} alt={partner.name} className="w-full h-full object-cover" />
        ) : (
          <>
            {remoteStream && <StreamVideo stream={remoteStream} muted={!speaker} className="w-full h-full object-cover" testId="video-remote-stream" />}
            {(!remoteStream || partnerMedia.cam_off) && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950" data-testid="video-remote-placeholder">
                <Avatar profile={partner} size="w-28 h-28" text="text-4xl" className="border border-white/10" testId="video-remote-avatar" />
                <div className="text-sm text-slate-300">{partnerMedia.cam_off && remoteStream ? `${partner.name} turned their camera off` : `Connecting to ${partner.name}…`}</div>
                {!remoteStream && <div className="text-xs font-mono text-slate-500">{RTC_LABEL[rtcState] || rtcState}</div>}
              </div>
            )}
          </>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/40 pointer-events-none" />
        <div className="orbit-scanline pointer-events-none" />
      </div>

      {/* Swipe-to-next hint / progress (touch devices) */}
      {dragX < 0 && (
        <div className="absolute inset-y-0 right-0 z-30 flex items-center pr-4 pointer-events-none" data-testid="video-swipe-indicator">
          <div className={`rounded-full px-4 py-2 font-bold text-sm flex items-center gap-2 transition-colors ${dragX <= -90 ? "bg-cyan-400 text-slate-950" : "bg-slate-950/70 text-cyan-300 border border-cyan-500/40"}`}>
            <SkipForward className="w-4 h-4" /> {dragX <= -90 ? "Release for next" : "Next"}
          </div>
        </div>
      )}
      {swipeHint && !dragX && (
        <div className="md:hidden absolute top-1/2 right-3 -translate-y-1/2 z-20 text-[10px] font-mono text-slate-300 bg-slate-950/60 rounded-full px-3 py-1 orbit-fade-up pointer-events-none" data-testid="video-swipe-hint">← swipe for next</div>
      )}

      {/* Floating reactions */}
      <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden" data-testid="reactions-layer">
        {reactions.map((r) => (
          <span key={r.id} className="orbit-reaction-float absolute bottom-40 text-4xl select-none" style={{ left: `${r.x}%` }} data-testid={`reaction-${r.from}`}>{r.emoji}</span>
        ))}
      </div>

      {status === "reconnecting" && (
        <div className="absolute inset-0 z-40 bg-slate-950/70 backdrop-blur-sm flex flex-col items-center justify-center gap-3" data-testid="video-reconnecting-overlay">
          <div className="w-14 h-14 rounded-full border-2 border-cyan-400/40 border-t-cyan-300 animate-spin" />
          <div className="font-display text-xl font-bold">Reconnecting to Orbit Hello…</div>
          <div className="text-xs font-mono text-slate-400">Your call is held for 25 seconds while your connection recovers.</div>
        </div>
      )}
      {partnerAway && status !== "reconnecting" && (
        <div className="absolute top-32 left-1/2 -translate-x-1/2 z-30 orbit-glass rounded-2xl px-4 py-3 flex items-center gap-3 border border-amber-500/40" data-testid="video-partner-reconnecting">
          <WifiOff className="w-4 h-4 text-amber-300" />
          <div className="text-sm"><span className="font-semibold">{partner.name}</span> lost connection — holding the call…</div>
        </div>
      )}

      {captionsOn && (
        <div className="absolute bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] md:bottom-28 left-3 right-3 md:left-1/2 md:right-auto md:-translate-x-1/2 z-20 md:w-[min(92vw,720px)] space-y-1.5 pointer-events-none" data-testid="video-captions-strip">
          {theirCaptions.map((c) => (
            <div key={c.id} className="orbit-glass rounded-xl px-4 py-2 border border-white/10" data-testid="caption-item-them">
              <div className="text-[10px] font-mono uppercase text-cyan-300 flex items-center gap-1"><Languages className="w-3 h-3" /> {partner.name} · {c.lang}{c.translated ? ` → ${c.target_lang}` : ""}</div>
              <div className={`text-base font-semibold ${c.final ? "text-slate-50" : "text-slate-300 italic"}`}>{c.translated || c.text}</div>
              {c.translated && <div className="text-xs text-slate-400 mt-0.5">{c.text}</div>}
            </div>
          ))}
          {myCaption && <div className="text-xs font-mono text-slate-400 text-center truncate" data-testid="caption-item-me">You: {myCaption.text}</div>}
          {!theirCaptions.length && !myCaption && <div className="text-xs font-mono text-slate-500 text-center">Captions on · {isDemo ? `listening in ${myLang}…` : `waiting for ${partner.name} to speak…`}</div>}
        </div>
      )}
      {!captionsOn && partnerWantsCaptions && (
        <div className="absolute bottom-[calc(env(safe-area-inset-bottom)+5.5rem)] md:bottom-28 left-1/2 -translate-x-1/2 z-20 text-[10px] font-mono text-slate-400 orbit-glass rounded-full px-3 py-1 whitespace-nowrap" data-testid="video-captioning-for-partner">
          <Captions className="w-3 h-3 inline mr-1" /> Captioning your speech for {partner.name}
        </div>
      )}

      {/* Top HUD */}
      <div className="absolute top-3 md:top-4 left-3 right-3 md:left-4 md:right-4 flex items-start justify-between z-20 gap-2" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap min-w-0">
          <div className="orbit-glass rounded-full px-2.5 py-1 md:px-3 md:py-1.5 text-sm flex items-center gap-1.5 md:gap-2 max-w-[60vw] md:max-w-none" data-testid="video-remote-identity">
            <span className="text-base md:text-lg">{partner.flag}</span>
            <span className="font-semibold truncate">{partner.name}</span>
            {partner.age && <span className="text-slate-400 hidden sm:inline">· {partner.age}</span>}
          </div>
          {isDemo ? (
            <div className="orbit-glass rounded-full px-2.5 py-1 md:px-3 md:py-1.5 text-[10px] md:text-xs font-mono text-amber-300 border border-amber-500/40 whitespace-nowrap" data-testid="video-demo-badge">DEMO MATCH<span className="hidden sm:inline"> — Simulated conversation</span></div>
          ) : (
            <div className={`orbit-glass rounded-full px-2.5 py-1 md:px-3 md:py-1.5 text-[10px] md:text-xs font-mono ${rtcState === "connected" ? "text-emerald-300" : "text-cyan-300"}`} data-testid="video-rtc-state">
              <span className="orbit-live-dot inline-block mr-1.5 align-middle" /> {RTC_LABEL[rtcState] || rtcState}{rtcState === "connected" && quality !== "720p" && <span className="ml-1.5 text-amber-300" data-testid="video-quality-badge">· {quality}</span>}
            </div>
          )}
          <div className="orbit-glass rounded-full px-2.5 py-1 md:px-3 md:py-1.5 text-[10px] md:text-xs font-mono text-slate-300" data-testid="video-duration">{fmt(duration)}</div>
          {partnerMedia.muted && !isDemo && <div className="orbit-glass rounded-full px-2.5 py-1 md:px-3 md:py-1.5 text-[10px] md:text-xs font-mono text-rose-300 flex items-center gap-1"><MicOff className="w-3 h-3" /><span className="hidden sm:inline">muted</span></div>}
          {audioRecovering && !isDemo && <div className="orbit-glass rounded-full px-2.5 py-1 md:px-3 md:py-1.5 text-[10px] md:text-xs font-mono text-amber-300 border border-amber-500/40 flex items-center gap-1.5 whitespace-nowrap" data-testid="video-audio-recovering"><span className="w-2.5 h-2.5 rounded-full border border-amber-300/40 border-t-amber-300 animate-spin" /> Reconnecting audio…</div>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={() => setReportOpen(true)} className="md:hidden orbit-glass rounded-full p-2 border border-white/10 text-rose-400" title="Report" data-testid="video-btn-report-block-mobile">
            <Flag className="w-4 h-4" />
          </button>
          <button onClick={call.endCall} className="orbit-glass rounded-full p-2 hover:border-rose-500/40 border border-white/10 text-rose-400" title="End" data-testid="video-btn-end">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {shared.length > 0 && (
        <div className="absolute top-16 md:top-20 left-3 md:left-4 z-20 orbit-fade-up max-w-[70vw] md:max-w-xs">
          <div className="orbit-glass rounded-2xl p-2.5 md:p-3">
            <div className="text-[10px] md:text-xs font-mono uppercase text-cyan-300 mb-0.5 md:mb-1">Matched because</div>
            <div className="text-xs md:text-sm text-slate-100 truncate">You both love <span className="text-cyan-300 font-semibold">{shared.join(", ")}</span></div>
          </div>
        </div>
      )}

      {icebreaker && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 orbit-fade-up hidden md:block max-w-lg" data-testid="video-ai-icebreaker-chip">
          <div className="orbit-glass rounded-full px-4 py-2 flex items-center gap-2 border border-cyan-500/30">
            <Sparkles className="w-4 h-4 text-cyan-300 shrink-0" />
            <span className="text-sm text-slate-100">{icebreaker}</span>
          </div>
        </div>
      )}

      {/* Self: bottom half on mobile (Monkey-style), right half on desktop */}
      <div className="absolute inset-x-0 bottom-0 h-1/2 bg-slate-900 overflow-hidden" data-testid="video-self-pip">
        {localStream && !camOff ? (
          <StreamVideo stream={localStream} muted mirror className="w-full h-full object-cover" testId="video-self-stream" />
        ) : (
          <div className="w-full h-full bg-slate-900 flex flex-col items-center justify-center text-slate-500 text-sm font-mono gap-2">
            <VideoOff className="w-8 h-8" />{camOff ? "CAMERA OFF" : "NO CAMERA"}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-3 left-3 md:top-4 md:left-4 orbit-glass rounded-full px-2.5 py-1 md:px-3 md:py-1.5 text-sm flex items-center gap-1.5 z-20" data-testid="video-self-identity">
          {user?.flag && <span className="text-base">{user.flag}</span>}
          <span className="font-semibold truncate max-w-[40vw]">{user?.display_name || "You"}</span>
          <span className="text-[10px] font-mono text-cyan-300">YOU</span>
          {muted && <MicOff className="w-3.5 h-3.5 text-rose-400" />}
        </div>
      </div>
      <div className="absolute inset-x-0 top-1/2 h-px bg-gradient-to-r from-cyan-400/60 via-fuchsia-500/60 to-cyan-400/60 z-10 pointer-events-none" />

      {/* Text chat: bottom sheet on mobile, sidebar on desktop */}
      {chatOpen && (
        <div className="absolute left-0 right-0 bottom-[calc(env(safe-area-inset-bottom)+4.5rem)] h-[52dvh] rounded-t-3xl md:rounded-none md:left-auto md:right-0 md:top-0 md:bottom-24 md:h-auto md:w-96 orbit-glass border-t md:border-t-0 md:border-l border-white/10 z-30 flex flex-col orbit-fade-up" data-testid="chat-sidebar">
          <div className="md:hidden mx-auto mt-2 w-10 h-1 rounded-full bg-white/20" />
          <div className="p-3 md:p-4 border-b border-white/10 flex items-center justify-between">
            <div className="font-display font-semibold">Chat with {partner.name}</div>
            <button onClick={() => setChatOpen(false)} className="text-slate-400 hover:text-slate-100"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={m.id || i} data-testid={`chat-message-${m.from}`} className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${MSG_STYLES[m.from] || MSG_STYLES.them}`}>
                {m.text}
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()}
              placeholder="Type a message…" className="flex-1 px-3 py-2 bg-slate-950/60 border border-white/10 rounded-full text-sm outline-none focus:border-cyan-400" data-testid="chat-input-field" />
            <button onClick={sendMsg} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 rounded-full w-9 h-9 flex items-center justify-center" data-testid="chat-send-btn">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Reaction palette */}
      {reactOpen && (
        <div className="absolute bottom-[calc(env(safe-area-inset-bottom)+5.25rem)] md:bottom-24 left-1/2 -translate-x-1/2 z-40 orbit-glass rounded-full px-2 md:px-3 py-1.5 md:py-2 flex items-center gap-0.5 md:gap-1 border border-white/10 orbit-fade-up max-w-[96vw]" data-testid="reaction-palette">
          {REACTIONS.map((e) => (
            <button key={e} onClick={() => { call.sendReaction(e); setReactOpen(false); }} className="w-9 h-9 md:w-10 md:h-10 rounded-full text-xl md:text-2xl hover:bg-white/10 active:scale-125 hover:scale-125 transition" data-testid={`reaction-btn-${e.codePointAt(0).toString(16)}`}>{e}</button>
          ))}
        </div>
      )}

      {/* Control dock */}
      <div className="absolute bottom-[calc(env(safe-area-inset-bottom)+0.75rem)] md:bottom-6 left-1/2 -translate-x-1/2 z-30 w-[calc(100%-1.5rem)] md:w-auto">
        <div className="orbit-glass rounded-full px-2 md:px-3 py-1.5 md:py-2 flex items-center justify-between md:justify-start gap-1 md:gap-2 shadow-2xl border border-white/10" data-testid="video-dock">
          <Dock onClick={call.toggleMic} active={muted} tone="rose" testId="video-btn-mute-mic">{muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}</Dock>
          <Dock onClick={call.toggleCam} active={camOff} tone="rose" testId="video-btn-toggle-cam">{camOff ? <VideoOff className="w-5 h-5" /> : <Video className="w-5 h-5" />}</Dock>
          {canFlip && !isDemo && <Dock onClick={flip} active={flipping} tone="cyan" testId="video-btn-flip-cam" title="Switch camera" className="md:hidden"><SwitchCamera className="w-5 h-5" /></Dock>}
          <Dock onClick={() => setSpeaker((s) => !s)} testId="video-btn-speaker" className="hidden md:flex">{speaker ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}</Dock>
          <Dock onClick={() => setChatOpen((c) => !c)} active={chatOpen} tone="cyan" testId="video-btn-toggle-chat"><MessageSquare className="w-5 h-5" /></Dock>
          <Dock onClick={() => setReactOpen((r) => !r)} active={reactOpen} tone="cyan" testId="video-btn-reactions"><SmilePlus className="w-5 h-5" /></Dock>
          <Dock onClick={onCaptions} active={captionsOn} tone="cyan" testId="video-btn-captions" title={isPremium ? "Live captions" : "Live captions (VIP / Galaxy)"}>
            <Captions className="w-5 h-5" />
            {!isPremium && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-fuchsia-500 flex items-center justify-center" data-testid="video-captions-lock"><Lock className="w-2.5 h-2.5 text-white" /></span>}
          </Dock>
          <button onClick={call.skip} className="h-10 md:h-11 px-4 md:px-5 rounded-full bg-gradient-to-r from-cyan-400 to-sky-500 text-slate-950 font-bold flex items-center gap-1.5 md:gap-2 shadow-lg shadow-cyan-500/40 active:scale-95 hover:scale-105 transition text-sm md:text-base" data-testid="video-btn-next-skip">
            <SkipForward className="w-4 h-4" /> Next
          </button>
          <button onClick={() => setReportOpen(true)} className="hidden md:flex w-11 h-11 rounded-full items-center justify-center bg-white/5 text-rose-400 hover:bg-rose-500/10" data-testid="video-btn-report-block">
            <Flag className="w-5 h-5" />
          </button>
        </div>
        <div className="hidden md:flex mt-2 text-center text-[10px] font-mono text-slate-500 items-center justify-center gap-1"><Radio className="w-3 h-3" /> {online} online</div>
      </div>

      {reportOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur flex items-center justify-center p-4" data-testid="report-modal">
          <div className="orbit-glass rounded-3xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-xl font-bold">Report user</div>
              <button onClick={() => setReportOpen(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-slate-400 mb-4">Reports are reviewed by our moderation team. The user will be blocked immediately.</p>
            <div className="space-y-2">
              {REPORT_REASONS.map((r) => (
                <button key={r.id} onClick={() => submitReport(r.id)}
                  className="w-full text-left px-4 py-3 rounded-xl bg-slate-900/60 hover:bg-rose-500/10 hover:border-rose-500/40 border border-white/10 text-slate-200 transition" data-testid={`report-reason-${r.id}`}>
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {upsellOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur flex items-center justify-center p-4" data-testid="premium-upsell-modal">
          <div className="orbit-glass rounded-3xl p-6 max-w-sm w-full text-center border border-fuchsia-500/30">
            <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-fuchsia-500 to-cyan-400 flex items-center justify-center"><Crown className="w-6 h-6 text-slate-950" /></div>
            <div className="font-display text-xl font-bold mt-4">Live captions are a VIP feature</div>
            <p className="text-sm text-slate-400 mt-2">Understand anyone in the world with real-time translated subtitles. Included in VIP Orbit and Galaxy Pass.</p>
            <button onClick={() => { exitingRef.current = true; call.disconnectAll(); nav("/pricing"); }} className="orbit-btn-primary w-full mt-5" data-testid="upsell-upgrade-btn">See plans</button>
            <button onClick={() => setUpsellOpen(false)} className="mt-3 text-xs text-slate-400 hover:text-slate-200" data-testid="upsell-close-btn">Not now</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Dock({ onClick, active, tone = "slate", testId, title, className = "", children }) {
  const tones = { rose: "bg-rose-500/20 text-rose-300 border border-rose-500/40", cyan: "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" };
  return (
    <button onClick={onClick} title={title} className={`relative w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center active:scale-90 transition-transform ${active ? tones[tone] : "bg-white/5 text-slate-200 hover:bg-white/10"} ${className}`} data-testid={testId}>
      {children}
    </button>
  );
}
